import { useEffect, useState } from "react";
import FormInput from "@/components/formInputFields/FormInput";
import FormCard from "@/components/formInputFields/FormCard";
import FormSectionTitle from "@/components/formInputFields/FormSectionTitle";

const InventorySection = ({ register, errors, setValue, watch }) => {
  // track the checkbox states dynamically using react hook form
  const trackQuantity = watch("trackInventory");
  const showSkuBarcode = watch("hasSKUBarcode");
  const watchedQty = watch("inventoryQuantity");

  // Local display state with comma formatting
  const [invText, setInvText] = useState("");

  // Helpers
  const formatWithCommas = (n) => {
    if (n === null || n === undefined || n === "") return "";
    const v = parseInt(String(n).replace(/,/g, ""), 10);
    if (isNaN(v)) return "";
    return v.toLocaleString();
  };

  const sanitizeDigits = (raw) => {
    if (raw == null) return "";
    const digits = String(raw).replace(/\D/g, "");
    if (digits === "") return "";
    const trimmed = digits.replace(/^0+/, "");
    return trimmed === "" ? "0" : trimmed;
  };

  // Sync local text when external value changes or toggle changes
  useEffect(() => {
    if (!trackQuantity) { setInvText(""); return; }
    setInvText(formatWithCommas(watchedQty));
  }, [watchedQty, trackQuantity]);

  const handleInvChange = (e) => {
    const cleaned = sanitizeDigits(e.target.value);
    setInvText(cleaned);
    // keep underlying value numeric; default to 0 when empty
    if (cleaned === "") {
      setValue("inventoryQuantity", 0, { shouldDirty: true, shouldValidate: false });
    } else {
      setValue("inventoryQuantity", parseInt(cleaned, 10), { shouldDirty: true, shouldValidate: false });
    }
  };

  const handleInvBlur = () => {
    setInvText((prev) => {
      if (prev === "") return "0"; // show 0 when left empty
      return formatWithCommas(prev);
    });
  };

  return (
    <FormCard className="w-full max-w-3xl">
      <FormSectionTitle>Inventory</FormSectionTitle>

      {/* Product tracking option */}
      <div className="mb-4">
        <label className="flex items-center gap-2 text-sm font-medium text-[#636363]">
          <input
            type="checkbox"
            // TODO: Change by increase size
            className="accent-[#000]"
            {...register("trackInventory")}
          />
          Track quantity
        </label>
      </div>

      {/* Product Inventory Quantity */}
      {trackQuantity && (
        <FormInput
          label="Inventory quantity"
          name="inventoryQuantity"
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          placeholder="0"
          value={invText}
          onChange={handleInvChange}
          onBlur={handleInvBlur}
          error={errors?.inventoryQuantity}
          onKeyDown={(e) => {
            if (["e", "E", "+", "-", "."].includes(e.key)) {
              e.preventDefault();
            }
          }}
        />
      )}

      {/* SKU and Barcode check */}
      <div className="mb-4 mt-6">
        <label className="flex items-center gap-2 text-sm font-medium text-[#636363]">
          <input
            type="checkbox"
            {...register("hasSKUBarcode")}
            // TODO: Change by increase size
            className="accent-[#000]"
          />
          This product has a SKU or Barcode
        </label>
      </div>

      {showSkuBarcode && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Product SKU field */}
          <FormInput
            label="SKU (Stock Keeping Unit)"
            name="productSku"
            register={register}
            error={errors?.productSku}
          />

          {/* Product barcode field */}
          {/* TODO: Confirm if these types are supported */}
          <FormInput
            label="Barcode (ISBN, UPC, GTIN, etc.)"
            name="productBarcode"
            register={register}
            error={errors?.productBarcode}
          />
        </div>
      )}
    </FormCard>
  );
};

export default InventorySection;
